import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Select, Space, Spin, Tag } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { Employee } from '../../../../entity/Employee';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import {
  DataTable,
  DataTableColumns,
  DataTablePagination,
} from '../../../../components/molecules/DataTable/DataTable';
import { QueryBuilder } from '../../../../components/molecules/QueryBuilder/QueryBuilder';
import { InfoView, InfoViewItem } from '../../../../components/info-view/InfoView';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { ClockCircleOutlined } from '@ant-design/icons';
import { UserAvatar, UserNameView } from '../../../../components/atoms/UserAvatar';
import { useHistory } from 'react-router-dom';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import Link from 'antd/lib/typography/Link';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { fullMark, sectionMark } from './PerformanceResultsPage';
import { useQueryParam } from 'use-query-params';

const searchSchema = [
  {
    id: 'email',
    name: 'Email',
    type: 'string',
  },
  // {
  //   id: 'employeeId',
  //   name: 'Employee Id',
  //   type: 'string',
  // },
  {
    id: 'epfNumber',
    name: 'EPF Number',
    type: 'string',
  },
];
// const colors = [
//   'magenta',
//   'red',
//   'volcano',
//   'orange',
//   'gold',
//   'lime',
//   'green',
//   'cyan',
//   'blue',
//   'geekblue',
//   'purple'
// ];

const roles = [
  { id: 'admin', value: 'Admin' },
  { id: 'hr', value: 'HR' },
  { id: 'management', value: 'Management' },
  { id: 'user', value: 'Normal User' },
];

const headers = [
  {
    label: 'EPF Number',
    key: 'epfNo',
  },
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  {
    label: 'Email',
    key: 'email',
  },
  {
    label: 'Rating',
    key: 'rating',
  },
  {
    label: 'Final Score',
    key: 'finalScore',
  },
  {
    label: 'Personal Score',
    key: 'personalScore',
  },
  {
    label: 'Peer Review Score',
    key: 'peerReviewScore',
  },
  {
    label: 'Internal Customer Score',
    key: 'internalCustomerScore',
  },
  {
    label: 'Team Score',
    key: 'teamScore',
  },
];

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';

const createXlsx = (csvData: any[], headers: any[], fileName: string, wscols: any[]) => {
  const h = Object.fromEntries(headers.map((e) => [e.key, e.label]));
  const ws = XLSX.utils.json_to_sheet([h], {
    header: headers.map((e) => e.key),
    skipHeader: true,
    // origin: 0, //ok
  });
  ws['!cols'] = wscols;
  XLSX.utils.sheet_add_json(
    ws,
    csvData.map((s) =>
      Object.fromEntries(
        Object.entries(s)
          .map(([key, val]) => ({ key, val }))
          .filter((e) => headers.find((n) => n.key === e.key))
          .map((x) => [x.key, x.val]),
      ),
    ),
    {
      header: headers.map((e) => e.key),
      skipHeader: true,
      origin: -1, //ok
    },
  );
  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

export const AllUserPerformancePage: FC = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const [query, setQuery] = useState<any>({});
  const [performanceCycles, setPerformanceCycles] = useState<any[]>([]);
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const [total, setTotal] = useState<number>(0);
  const [department, setDepartment] = useState<number | undefined>();
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [selected, setSelected] = useState<Employee>();
  const user2 = useUserInfo();

  const connection = useConnection();
  const history = useHistory();

  const columns: DataTableColumns<any> = useMemo(() => {
    return [
      {
        title: 'User',
        dataIndex: 'image',
        key: 'image',
        // eslint-disable-next-line react/display-name
        render: (value, record) => (
          <Space>
            <UserAvatar src={value} />
            <Link
              onClick={() => {
                setSelected(record);
                // setUser(record);
              }}>
              {record.firstName} {record.lastName}
            </Link>
          </Space>
        ),
      },
      // {
      //   title: 'Email',
      //   dataIndex: 'email',
      //   key: 'email',
      // },
      // {
      //   title: 'Employee Id',
      //   dataIndex: 'employeeId',
      //   key: 'employeeId',
      // },
      {
        title: 'EPF Number',
        dataIndex: 'epfNo',
        key: 'epfNumber',
      },
      {
        title: 'Final Score',
        dataIndex: 'finalScore',
        key: 'finalScore',
      },
      {
        title: 'Personal Score',
        dataIndex: 'personalScore',
        key: 'personalScore',
      },
      {
        title: 'Peer Review Score',
        dataIndex: 'peerReviewScore',
        key: 'peerReviewScore',
      },
      {
        title: 'Internal Customer Score',
        dataIndex: 'internalCustomerScore',
        key: 'internalCustomerScore',
      },
      {
        title: 'Team Score',
        dataIndex: 'teamScore',
        key: 'teamScore',
      },
      {
        title: 'Rating',
        dataIndex: 'rating',
        key: 'rating',
      },
      {
        title: 'Action',
        dataIndex: 'id',
        key: 'id',
        // eslint-disable-next-line react/display-name
        render: (name: Text, record: any) => (
          <Button
            onClick={() => {
              history.push(`/webux/performance-result/employee/${record.id}?cycle=${cycle}`);
            }}>
            Performance
          </Button>
        ),
      },
    ];
  }, [cycle, history]);

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setPerformanceCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  const search = useCallback(
    (query: any, pagination: DataTablePagination) => {
      setLoading(true);
      if (cycle) {
        connection
          .get(
            `users/all/performance/${cycle}`,
            department || department !== 0
              ? { ...pagination, ...query, department: department }
              : { ...pagination, ...query },
          )
          .then((value) => {
            setTableData(
              value.data.map((e: { user: any; teams: any }) => ({
                ...e.user,
                finalScore: e?.teams?.find(
                  (y: {
                    supervisorFeedBack: { answer: any };
                    peerReviews: { answer: any };
                    internalCustomers: { answer: any };
                    teamPerformance: { answer: any };
                  }) =>
                    typeof y.supervisorFeedBack?.answer === 'string' ||
                    typeof y.peerReviews?.answer === 'string' ||
                    typeof y.internalCustomers?.answer === 'string' ||
                    typeof y.teamPerformance?.answer === 'string',
                ) ? (
                  <Tag icon={<ClockCircleOutlined />} color="default">
                    Pending
                  </Tag>
                ) : (
                  `${fullMark(e?.teams).toFixed(2)}%`
                ),
                personalScore: e?.teams.find(
                  (y: {
                    supervisorFeedBack: { answer: any };
                    peerReviews: { answer: any };
                    internalCustomers: { answer: any };
                    teamPerformance: { answer: any };
                  }) => typeof y.supervisorFeedBack?.answer === 'string',
                ) ? (
                  <Tag icon={<ClockCircleOutlined />} color="default">
                    Pending
                  </Tag>
                ) : (
                  `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.supervisorFeedBack,
                    })),
                  ).toFixed(2)}%`
                ),
                teamScore: e?.teams.find(
                  (y: {
                    supervisorFeedBack: { answer: any };
                    peerReviews: { answer: any };
                    internalCustomers: { answer: any };
                    teamPerformance: { answer: any };
                  }) => typeof y.teamPerformance?.answer === 'string',
                ) ? (
                  <Tag icon={<ClockCircleOutlined />} color="default">
                    Pending
                  </Tag>
                ) : (
                  `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.teamPerformance,
                    })),
                  ).toFixed(2)}%`
                ),
                peerReviewScore: e?.teams.find(
                  (y: {
                    supervisorFeedBack: { answer: any };
                    peerReviews: { answer: any };
                    internalCustomers: { answer: any };
                    teamPerformance: { answer: any };
                  }) => typeof y.peerReviews?.answer === 'string',
                ) ? (
                  <Tag icon={<ClockCircleOutlined />} color="default">
                    Pending
                  </Tag>
                ) : (
                  `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.peerReviews,
                    })),
                  ).toFixed(2)}%`
                ),
                internalCustomerScore: e?.teams.find(
                  (y: {
                    supervisorFeedBack: { answer: any };
                    peerReviews: { answer: any };
                    internalCustomers: { answer: any };
                    teamPerformance: { answer: any };
                  }) => typeof y.internalCustomers?.answer === 'string',
                ) ? (
                  <Tag icon={<ClockCircleOutlined />} color="default">
                    Pending
                  </Tag>
                ) : (
                  `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.internalCustomers,
                    })),
                  ).toFixed(2)}%`
                ),
                rating:
                  e?.teams?.[0]?.grade === 'Pending' ? (
                    <Tag icon={<ClockCircleOutlined />} color="default">
                      Pending
                    </Tag>
                  ) : (
                    e?.teams?.[0]?.grade ?? (
                      <Tag icon={<ClockCircleOutlined />} color="default">
                        Pending
                      </Tag>
                    )
                  ),
              })),
            );
            setTotal(value.total);
          })
          .finally(() => setLoading(false));
      }
    },
    [connection, cycle, department],
  );

  const exportValue = useCallback(
    (query: any) => {
      setLoading(true);
      setCsvLoading(true);
      if (cycle) {
        connection
          .get(
            `users/all/performance/${cycle}`,
            department || department !== 0
              ? { ...query, exportValue: true, department: department }
              : { ...query, exportValue: true },
          )
          .then((value) => {
            const csvData = value.data.map((e: { user: any; teams: any }) => ({
              ...e.user,
              finalScore: e?.teams?.find(
                (y: {
                  supervisorFeedBack: { answer: any };
                  peerReviews: { answer: any };
                  internalCustomers: { answer: any };
                  teamPerformance: { answer: any };
                }) =>
                  typeof y.supervisorFeedBack?.answer === 'string' ||
                  typeof y.peerReviews?.answer === 'string' ||
                  typeof y.internalCustomers?.answer === 'string' ||
                  typeof y.teamPerformance?.answer === 'string',
              )
                ? 'Pending'
                : `${fullMark(e?.teams).toFixed(2)}%`,
              personalScore: e?.teams.find(
                (y: {
                  supervisorFeedBack: { answer: any };
                  peerReviews: { answer: any };
                  internalCustomers: { answer: any };
                  teamPerformance: { answer: any };
                }) => typeof y.supervisorFeedBack?.answer === 'string',
              )
                ? 'Pending'
                : `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.supervisorFeedBack,
                    })),
                  ).toFixed(2)}%`,
              teamScore: e?.teams.find(
                (y: {
                  supervisorFeedBack: { answer: any };
                  peerReviews: { answer: any };
                  internalCustomers: { answer: any };
                  teamPerformance: { answer: any };
                }) => typeof y.teamPerformance?.answer === 'string',
              )
                ? 'Pending'
                : `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.teamPerformance,
                    })),
                  ).toFixed(2)}%`,
              peerReviewScore: e?.teams.find(
                (y: {
                  supervisorFeedBack: { answer: any };
                  peerReviews: { answer: any };
                  internalCustomers: { answer: any };
                  teamPerformance: { answer: any };
                }) => typeof y.peerReviews?.answer === 'string',
              )
                ? 'Pending'
                : `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.peerReviews,
                    })),
                  ).toFixed(2)}%`,
              internalCustomerScore: e?.teams.find(
                (y: {
                  supervisorFeedBack: { answer: any };
                  peerReviews: { answer: any };
                  internalCustomers: { answer: any };
                  teamPerformance: { answer: any };
                }) => typeof y.internalCustomers?.answer === 'string',
              )
                ? 'Pending'
                : `${sectionMark(
                    e.teams.map((e1: any) => ({
                      cyclePercentage: e1.cyclePercentage,
                      section: e1.internalCustomers,
                    })),
                  ).toFixed(2)}%`,
              rating:
                e?.teams?.[0]?.grade === 'Pending' ? 'Pending' : e?.teams?.[0]?.grade ?? 'Pending',
            }));
            const wscols = headers.map((e) => ({
              wch: Math.max(
                e.label.length + 2,
                Math.max(...csvData.map((d: any) => (d[e.key] + '   ').length)),
              ),
            }));
            createXlsx(
              csvData,
              headers,
              performanceCycles.find((e) => e.id == cycle).name + '_Users_Final_Score',
              wscols,
            );
            setTotal(value.total);
          })
          .finally(() => {
            setLoading(false);
            setCsvLoading(false);
          });
      } else {
        setLoading(false);
        setCsvLoading(false);
      }
    },
    [connection, cycle, department, performanceCycles],
  );

  useEffect(() => {
    search({}, { page: 0, size: 10 });
  }, [search]);

  useEffect(() => {
    if (cycle) {
      connection.get(`common/department/${cycle}`).then((value: any) => {
        setDepartments(value?.departments);
      });
    }
  }, [cycle, connection]);

  // useEffect(() => {
  //   const teams: any[] = [];
  //   connection.get('teams').then((val) => {
  //     val.teams.map((team: { id: number; teamName: string }, key: number) => {
  //       teams.push({ id: team.id, name: team.teamName, color: colors[key % colors.length] });
  //     });
  //   });
  //   console.log(teams);
  // }, [connection]);
  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Performance Results"
        subTitle="View employee performance result details"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {performanceCycles && (
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => {
                setCycle(value, 'replace');
                setDepartment(undefined);
              }}
              data={performanceCycles}
            />
          )}
        </WebUxPageItem>
        <WebUxPageItem>
          <Spin spinning={loading}>
            <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
              <FlexBox horizontalAlign={'left'} style={{ justifyContent: 'space-between' }}>
                <QueryBuilder
                  searchPlaceholder="Search Employee"
                  search={true}
                  value={query}
                  onChange={setQuery}
                  onSearch={(value) => search(value, pagination)}
                  schema={searchSchema}
                />
                {!loading && (user2?.roles?.includes('admin') || user2?.roles?.includes('hr')) ? (
                  <Select
                    placeholder="Select Team"
                    style={{ width: '200px' }}
                    onMouseDown={(e) => e.preventDefault()}
                    onChange={(val) => setDepartment(val)}
                    defaultValue={department}>
                    <Select.Option key={0} value={0}>
                      All
                    </Select.Option>
                    {departments.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <div></div>
                )}
                <Space>
                  <Button
                    onClick={() => {
                      setDepartment(undefined);
                      setQuery(undefined);
                      search(undefined, { page: 1, size: pagination.size });
                    }}>
                    Reset
                  </Button>
                  <Button type="primary" onClick={() => search(query, pagination)}>
                    Search
                  </Button>
                  <Button
                    loading={csvLoading}
                    key="1"
                    type="primary"
                    onClick={() => exportValue(query)}>
                    Export Data
                  </Button>
                </Space>
              </FlexBox>
              <DataTable<Employee>
                rowKey={'id'}
                // loading={loading}
                columns={columns}
                dataSource={tableData}
                total={total}
                // onClick={(item) => {
                //   setSelected(item);
                //   setUser(item);
                // }}
                onPaginationChange={(p) => {
                  setPagination(p);
                  if (pagination.page !== p.page || pagination.size !== p.size) {
                    search(query, p);
                  }
                }}
                dtPagination={pagination}
              />
            </Space>
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={!!selected}
        title="View Employee"
        destroyOnClose={true}
        onCancel={() => setSelected(undefined)}
        onOk={() => setSelected(undefined)}
        footer={
          <Space align={'end'}>
            {(user2?.roles?.includes('admin') || user2?.roles?.includes('hr')) && (
              <>
                <Button
                  onClick={() => {
                    history.push(
                      `/webux/employees/${selected?.id}/${encodeURIComponent(
                        selected?.firstName ?? '',
                      )} ${encodeURIComponent(selected?.lastName ?? '')}/time-line`,
                    );
                    setSelected(undefined);
                  }}>
                  TimeLine
                </Button>
              </>
            )}
            <Button
              type="primary"
              onClick={() => {
                setSelected(undefined);
              }}>
              Close
            </Button>
          </Space>
        }>
        <InfoView>
          <InfoViewItem fullwidth title="">
            {<UserAvatar src={selected?.image} size={100} />}
          </InfoViewItem>
          <InfoViewItem title="First Name">{selected?.firstName}</InfoViewItem>
          <InfoViewItem title="Last Name">{selected?.lastName}</InfoViewItem>
          {/*<InfoViewItem title="Employee ID">{selected?.employeeId}</InfoViewItem>*/}
          <InfoViewItem title="EPF Number">{selected?.epfNo}</InfoViewItem>
          <InfoViewItem title="Email">{selected?.email}</InfoViewItem>
          <InfoViewItem title="Designation">{selected?.designationName}</InfoViewItem>
          <InfoViewItem title="Date of Join">{selected?.doj}</InfoViewItem>
          <InfoViewItem title="Goals Availability">{selected?.goalsAvailability + ''}</InfoViewItem>
          {/*<InfoViewItem title="Supervisor">{selected?.supervisorName}</InfoViewItem>*/}
          <InfoViewItem title="Roles">
            {selected?.roles?.map((value1, index) => (
              <UserNameView
                name={roles.find((e) => e.id == value1)?.value ?? value1}
                tags={true}
                key={index}
              />
            ))}
          </InfoViewItem>
          <InfoViewItem title="Teams">
            {selected?.teams
              ?.filter((e) => e.cycleId == cycle)
              .map((value1, index) => (
                <UserNameView
                  position={value1?.position}
                  src={value1.image}
                  name={value1.name}
                  tags={true}
                  key={index}
                />
              ))}
          </InfoViewItem>
        </InfoView>
      </Modal>
    </WebUxPage>
  );
};
